import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { errorText, filterText } from '../../pages/SoftwarePolicy/policyEdit';
import { existingRule, inventoryUrl, itemFromPackage, searchUrl } from '../../pages/SoftwareBuilder/builderLogic';
import PolicyValue from './PolicyValue';
import { knownPackages, packagesOf } from './softwareBladeLogic';

const list = (v) => (Array.isArray(v) ? v : []);

// The blade on the right: software to put in one profile. Ticked, it lists the software installed on the machines of the profile; cleared,
// it searches all of the software list. Each line has the same In policy link as the profile itself.
export default function AddSoftwareBlade({ account, ruleSet, editable, lockedReason, busyKey, onSet, onClose }) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const [text, setText] = useState('');
  const [installedOnly, setInstalledOnly] = useState(true);
  const [inventory, setInventory] = useState({ state: 'loading' });
  const [found, setFound] = useState({ state: 'idle', packages: [] });

  useEffect(() => {
    let cancelled = false;
    apiRef.current.request('GET', inventoryUrl(account, ruleSet.filter, { cve: false }))
      .then((r) => { if (!cancelled) setInventory({ state: 'ok', ...r.data }); })
      .catch((ex) => { if (!cancelled) setInventory({ state: 'error', error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [account, ruleSet.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = text.trim();
    if (installedOnly || q.length < 2) { setFound({ state: 'idle', packages: [] }); return undefined; }
    let cancelled = false;
    setFound((f) => ({ ...f, state: 'loading' }));
    const timer = setTimeout(() => {
      apiRef.current.request('GET', searchUrl(q))
        .then((r) => { if (!cancelled) setFound({ state: 'ok', packages: list(r.data?.packages) }); })
        .catch((ex) => { if (!cancelled) setFound({ state: 'error', packages: [], error: errorText(ex) }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [text, installedOnly]);

  const programs = useMemo(() => list(inventory.programs), [inventory]);
  const words = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const installed = knownPackages(programs).filter((pkg) => words.every((w) => `${pkg.id} ${pkg.name}`.toLowerCase().includes(w)));
  const packages = installedOnly ? installed : found.packages;
  const installedBy = (id) => programs.find((p) => packagesOf(p).some((pkg) => pkg.id === id) || p.definition?.key === id);
  const loading = installedOnly ? inventory.state === 'loading' : found.state === 'loading';

  return (
    <>
      <div className="v2-scrim" onClick={onClose} role="presentation" />
      <aside className="v2-blade" role="dialog" aria-label="Add software">
        <header>
          <div>
            <h2>Add software</h2>
            <div className="v2-sub">For {filterText(ruleSet.filter)}</div>
          </div>
          <button type="button" className="v2-iconbtn" onClick={onClose} aria-label="Close">&times;</button>
        </header>
        <div className="body">
          <input className="v2-input" type="search" aria-label="Search software" placeholder="Search by name or keyword, for example storage explorer" value={text} onChange={(e) => setText(e.target.value)} />
          <label className="v2-check"><input type="checkbox" checked={installedOnly} onChange={(e) => setInstalledOnly(e.target.checked)} /> Only software installed on these machines</label>
          {loading && <div><span className="v2-spin" role="status" aria-label="Loading" /></div>}
          {installedOnly && inventory.state === 'error' && <div className="v2-alert err">{inventory.error}</div>}
          {!installedOnly && found.state === 'error' && <div className="v2-alert err">{found.error}</div>}
          {!installedOnly && found.state === 'idle' && <div className="v2-hint">Type at least two characters to search.</div>}
          {packages.length > 0 ? (
            <table className="v2-table" aria-label="Software packages">
              <thead><tr><th>Software</th><th>Newest</th><th>In policy</th></tr></thead>
              <tbody>
                {packages.map((pkg) => {
                  const program = installedBy(pkg.id);
                  const key = program?.definition?.key || pkg.id;
                  const rule = existingRule(ruleSet, key);
                  return (
                    <tr key={pkg.id}>
                      <td style={{ fontWeight: 600 }}>{pkg.name}</td>
                      <td>{pkg.latestVersion}</td>
                      <td>
                        <PolicyValue name={pkg.name} value={rule?.mode} busy={busyKey === key} disabled={!editable || busyKey !== null} title={lockedReason}
                          onChange={(value) => onSet(itemFromPackage(pkg, program), value)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            !loading && (installedOnly ? inventory.state === 'ok' : found.state === 'ok') && <div className="v2-empty">No software matches.</div>
          )}
          <div className="v2-hint">The list is refreshed every night.</div>
        </div>
      </aside>
    </>
  );
}
